"use client";

import { useState } from "react";
import Image from "@/components/ui/OptimizedImage";
import Link from "next/link";
import { Minus, Plus, Trash2 } from "lucide-react";

import { usePageTranslation } from "@/hooks/usePageTranslation";
import { getImageUrl } from "@/lib/utils";
import { formatPrice } from "@/lib/utils/cart-calculations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCartStore, type CartItem as CartItemType } from "@/store/cart";

interface CartItemProps {
  item: CartItemType;
  currencyIcon?: string;
}

export function CartItem({ item, currencyIcon = "৳" }: CartItemProps) {
  const { t } = usePageTranslation();
  const updateQuantity = useCartStore((state) => state.updateQuantity);
  const removeItem = useCartStore((state) => state.removeItem);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleQuantityChange = async (newQuantity: number) => {
    if (newQuantity < 1) return;

    setIsUpdating(true);
    try {
      updateQuantity(item.id, newQuantity, item.variantId);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemove = () => {
    removeItem(item.id, item.variantId);
  };

  const itemTotal = item.price * item.quantity;
  const hasDiscount = item.originalPrice && item.originalPrice > item.price;
  const discountAmount = hasDiscount
    ? (item.originalPrice! - item.price) * item.quantity
    : 0;

  const isOutOfStock = item.stock !== undefined && item.stock <= 0;
  const isLowStock =
    item.stock !== undefined && item.stock > 0 && item.stock < 5;

  return (
    <div className="cart-item rounded-brand border border-brand-border bg-brand-surface p-2.5 transition-colors hover:bg-brand-fg/5 sm:p-4 overflow-hidden">
      <div className="flex gap-3 sm:gap-4">
        {/* Product Image */}
        <Link
          href={`/product/${item.slug}`}
          className="cart-item__image flex-shrink-0"
        >
          <div className="relative h-20 w-20 overflow-hidden rounded-brand border border-brand-border bg-brand-fg/5 sm:h-24 sm:w-24">
            <Image
              src={getImageUrl(item.image || "/placeholder-image.jpg")}
              alt={item.name}
              fill
              className="object-cover"
              sizes="(max-width: 640px) 80px, 96px"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/placeholder-image.jpg";
              }}
            />
          </div>
        </Link>

        {/* Product Details */}
        <div className="cart-item__details flex-grow min-w-0">
          {/* Name and Price Row */}
          <div className="flex justify-between gap-2">
            <div className="min-w-0 flex-grow">
              <Link
                href={`/product/${item.slug}`}
                className="cart-item__name line-clamp-2 text-sm font-semibold text-brand-fg transition-colors hover:text-brand-primary sm:text-base"
              >
                {item.name}
                {item.variant && (
                  <span className="text-brand-muted"> - {item.variant}</span>
                )}
              </Link>

              <div className="mt-0.5 flex flex-wrap items-center gap-1 sm:gap-2">
                {item.brand && (
                  <p className="cart-item__brand text-xs text-brand-muted">
                    {item.brand}
                  </p>
                )}
                {item.vatPercentage !== undefined && item.vatPercentage !== null && item.vatPercentage > 0 && (
                  <span className="inline-flex items-center rounded-brand-sm bg-brand-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-primary sm:text-xs">
                    {t("cart.vatBadge", { percent: item.vatPercentage })}
                  </span>
                )}
              </div>

              {/* Stock Status */}
              {isOutOfStock && (
                <p className="mt-1 text-xs font-medium text-brand-danger">
                  {t("product.outOfStock")}
                </p>
              )}
              {isLowStock && (
                <p className="mt-1 text-xs font-medium text-brand-warning">
                  {t("product.lowStock", { count: item.stock ?? 0 })}
                </p>
              )}
            </div>

            {/* Price - Hidden on mobile, shown on sm+ */}
            <div className="cart-item__price hidden flex-shrink-0 text-end sm:block">
              <div className="text-base font-bold text-brand-fg">
                {formatPrice(item.price, currencyIcon)}
              </div>
              {hasDiscount && (
                <div className="text-xs text-brand-muted line-through">
                  {formatPrice(item.originalPrice!, currencyIcon)}
                </div>
              )}
            </div>
          </div>

          {/* Quantity Controls & Actions */}
          <div className="cart-item__actions mt-2 flex flex-wrap items-center justify-between gap-2 sm:mt-3">
            {/* Quantity Controls */}
            <div className="cart-item__quantity-controls flex items-center gap-1.5 sm:gap-2">
              <button
                onClick={() => handleQuantityChange(item.quantity - 1)}
                disabled={isUpdating || item.quantity <= 1}
                className="flex h-8 w-8 items-center justify-center rounded-brand-sm border border-brand-border transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t("cart.decreaseQuantity")}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>

              <span className="cart-item__quantity min-w-[32px] text-center text-sm font-semibold text-brand-fg">
                {item.quantity}
              </span>

              <button
                onClick={() => handleQuantityChange(item.quantity + 1)}
                disabled={
                  isUpdating ||
                  (item.maxQuantity !== undefined &&
                    item.quantity >= item.maxQuantity) ||
                  (item.stock !== undefined && item.quantity >= item.stock)
                }
                className="flex h-8 w-8 items-center justify-center rounded-brand-sm border border-brand-border transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={t("cart.increaseQuantity")}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Item Total & Remove */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="text-end">
                <div className="text-sm font-bold text-brand-primary sm:text-base">
                  {formatPrice(itemTotal, currencyIcon)}
                </div>
                {hasDiscount && (
                  <div className="text-[10px] text-brand-success sm:text-xs">
                    {t("cart.saveAmount", { amount: formatPrice(discountAmount, currencyIcon) })}
                  </div>
                )}
              </div>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="cart-item__remove rounded-brand-sm p-1.5 text-brand-danger transition-colors hover:bg-brand-danger/10"
                    aria-label={t("cart.removeItem")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-[90vw] sm:max-w-lg">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("cart.removeItemTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("cart.removeItemConfirm", { name: item.name })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleRemove}
                      className="bg-brand-danger text-brand-danger-foreground hover:bg-brand-danger/90 focus:ring-brand-danger"
                    >
                      {t("cart.remove")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
